import { FC } from 'react';
import { Box, withStyles, formatAmount, Tooltip } from 'vopay';
import { DetailRowsCard } from 'UI';
import { useTranslation } from 'react-i18next';
import { Balance } from '@/hooks/useApi/useAccountBalances';
import ActionsMenu from '../AccountUI/ActionsMenu';
import styles from './BalanceDetailsCard.module.scss';

const cn = withStyles(styles);

type BalanceDetailsCardProps = {
  balance: Balance;
};

const BalanceDetailsCard: FC<BalanceDetailsCardProps> = ({ balance }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances' });

  return (
    <DetailRowsCard
      title={
        <div className={cn('detailsCardTitle')}>
          <div>{t('details', { defaultValue: 'Details' })}</div>

          <ActionsMenu currency={balance.Currency} />
        </div>
      }
      details={[
        {
          label: t('accountBalance', { defaultValue: 'Account Balance' }),
          value: (
            <span className={cn(balance.AccountBalance < 0 && 'negative')}>
              {balance.Currency} {formatAmount(balance.AccountBalance)}
            </span>
          ),
        },
        {
          label: t('pendingBalance', { defaultValue: 'Pending Balance' }),
          value: (
            <span className={cn(balance.PendingFunds < 0 && 'negative')}>
              {balance.Currency} {formatAmount(balance.PendingFunds)}
            </span>
          ),
        },
        {
          label: t('reserveAmount', { defaultValue: 'Reserve Amount' }),
          value: (
            <span className={cn(balance.Reserve < 0 && 'negative')}>
              {balance.Currency} {formatAmount(balance.Reserve)}
            </span>
          ),
        },
        {
          label: (
            <Box gapSize="4px" ai="center">
              <span>{t('rollingReserveNsf', { defaultValue: 'Rolling Reserve NSF' })}</span>

              <Tooltip
                position="right"
                icon="info"
                information={
                  <Box gapSize="8px" flx="col">
                    <span className={cn('tooltipTitle')}>
                      {t('rollingReserveNsf', { defaultValue: 'Rolling Reserve NSF' })}
                    </span>
                    <p className={cn('message')}>
                      {t('rollingReserveNsfInfo', {
                        defaultValue:
                          'Indicates the portion of the account balance which is being held as a reserve against NSF transactions.',
                      })}
                    </p>
                  </Box>
                }
              />
            </Box>
          ),
          value: (
            <span className={cn(balance.RollingReserveNSF < 0 && 'negative')}>
              {balance.Currency} {formatAmount(balance.RollingReserveNSF)}
            </span>
          ),
        },
        {
          label: (
            <Box gapSize="4px" ai="center">
              <span>{t('rollingReserveReturns', { defaultValue: 'Rolling Reserve Returns' })}</span>

              <Tooltip
                position="right"
                icon="info"
                information={
                  <Box gapSize="8px" flx="col">
                    <span className={cn('tooltipTitle')}>
                      {t('rollingReserveReturns', { defaultValue: 'Rolling Reserve Returns' })}
                    </span>
                    <p className={cn('message')}>
                      {t('rollingReserveReturnsInfo', {
                        defaultValue:
                          'Indicates the portion of the account balance which is being held as a reserve against returned transactions.',
                      })}
                    </p>
                  </Box>
                }
              />
            </Box>
          ),
          value: (
            <span className={cn(balance.RollingReserveReturns < 0 && 'negative')}>
              {balance.Currency} {formatAmount(balance.RollingReserveReturns)}
            </span>
          ),
        },
        {
          label: (
            <Box gapSize="4px" ai="center">
              <span>{t('availableBalance', { defaultValue: 'Available Balance' })}</span>

              <Tooltip
                position="right"
                icon="info"
                information={
                  <Box gapSize="8px" flx="col">
                    <span className={cn('tooltipTitle')}>
                      {t('availableBalance', { defaultValue: 'Available Balance' })}
                    </span>
                    <p className={cn('message')}>
                      {t('availableBalanceInfo', {
                        defaultValue: 'Indicates the portion of the account balance that is available immediately.',
                      })}
                    </p>
                  </Box>
                }
              />
            </Box>
          ),
          value: (
            <span className={cn(balance.AvailableFunds < 0 && 'negative')}>
              {balance.Currency} {formatAmount(balance.AvailableFunds)}
            </span>
          ),
        },
      ]}
    />
  );
};

export default BalanceDetailsCard;
